import { useEffect, useRef, useState } from "react";
import { Eraser, Check, X as CloseIcon } from "@phosphor-icons/react";

/**
 * SignaturePad — mouse + touch signature canvas.
 *
 * Props:
 *   onSave({ dataUrl, signerName })  — called when user taps Save
 *   onCancel()                       — called when user taps X
 *   defaultName?: string
 */
export default function SignaturePad({ onSave, onCancel, defaultName = "" }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(false);
  const [signerName, setSignerName] = useState(defaultName);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      // Preserve any existing content
      const prev = canvas.toDataURL();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, rect.width, rect.height);
      if (hasInk) {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
        img.src = prev;
      }
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.strokeStyle = "#0f172a";
      ctx.lineWidth = 2.5;
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pointFrom = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pointFrom(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = pointFrom(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (!hasInk) setHasInk(true);
  };

  const end = () => { drawingRef.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasInk(false);
  };

  const save = async () => {
    if (!hasInk) return;
    if (!signerName.trim()) return;
    setSaving(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await onSave({ dataUrl, signerName: signerName.trim() });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="signature-pad-modal">
      <div className="w-full max-w-lg rugged-card p-5 bg-white">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-black text-xl uppercase">Customer Sign-Off</h3>
          <button onClick={onCancel} data-testid="signature-close-btn" className="text-slate-500 hover:text-[#0f172a]">
            <CloseIcon weight="bold" size={22}/>
          </button>
        </div>
        <p className="mt-1 text-xs font-bold text-slate-500 uppercase tracking-wider">
          Ask the customer to sign in the box, then type their printed name.
        </p>
        <div className="mt-4 border-2 border-[#0f172a] bg-white touch-none" style={{ height: 220 }} data-testid="signature-canvas-wrap">
          <canvas
            ref={canvasRef}
            className="w-full h-full block cursor-crosshair"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            data-testid="signature-canvas"
          />
        </div>
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            {hasInk ? "Looks good?" : "Sign inside the box"}
          </span>
          <button onClick={clear} disabled={!hasInk} data-testid="signature-clear-btn"
            className="h-9 px-3 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 disabled:opacity-40 bg-white">
            <Eraser weight="bold" size={14}/> Clear
          </button>
        </div>
        <label className="mt-4 block">
          <span className="text-xs font-black uppercase tracking-wider">Printed name *</span>
          <input value={signerName} onChange={e => setSignerName(e.target.value)}
            data-testid="signature-name-input"
            placeholder="Jane Doe"
            className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-medium"/>
        </label>
        <button onClick={save} disabled={saving || !hasInk || !signerName.trim()}
          data-testid="signature-save-btn"
          className="tactile-btn mt-4 w-full h-12 bg-[#00C4CC] text-white font-black uppercase text-sm tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-50">
          <Check weight="bold" size={16}/> {saving ? "Saving..." : "Save Signature"}
        </button>
        <p className="mt-2 text-[11px] text-slate-500 font-medium text-center">
          Signature is embedded into the PDF work order and cannot be edited after saving.
        </p>
      </div>
    </div>
  );
}
